import {
  Alert,
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, Show, Space, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {
  ArticleCategoryBase,
  GetArticleCategoryResponse,
  usePatchArticleCategoryMutation,
  usePostArticleCategoryMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError, useDuplicateErrorHandler} from '@dms/shared';

import {ApiError, Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {ArticleCategoryForm, ArticleCategoryFormSchema} from '../types/ArticleCategoryForm';

export interface ArticleCategoryDetailFormProps extends RequiredTestIdProps {
  id: string | Nullish;
  articleCategory: GetArticleCategoryResponse | Nullish;
  isEditDisabled: boolean;
}

export function ArticleCategoryDetailForm(props: ArticleCategoryDetailFormProps) {
  const navigate = useNavigate();

  const {duplicateError, duplicateErrorHandler} = useDuplicateErrorHandler();

  const [postArticleCategory] = usePostArticleCategoryMutation();
  const [patchArticleCategory] = usePatchArticleCategoryMutation();

  const handleSubmit: FormSubmitHandler<ArticleCategoryForm> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const requestBody: ArticleCategoryBase = {
      ...formValues,
      system: false,
    };

    if (isNotNil(props.articleCategory)) {
      return await patchArticleCategory({articleCategoryId: props.id as string, body: requestBody})
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          handleNavigateBack();
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await postArticleCategory({body: requestBody})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyCreated'));
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        handleNavigateBack();
      })
      .catch(duplicateErrorHandler);
  };

  const handleNavigateBack = () => {
    navigate(settingsRoutes.warehousesArticleCategory);
  };

  const defaultValues: Partial<ArticleCategoryForm> = {
    name: props.articleCategory?.name,
    code: props.articleCategory?.code,
    active: props.articleCategory?.active ?? true,
    system: props.articleCategory?.system ?? false,
    isDefault: props.articleCategory?.isDefault ?? false,
  };

  return (
    <Form<ArticleCategoryForm>
      experimentalZodSchema={ArticleCategoryFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isDisabled={props.isEditDisabled}
      shouldWatchForUnsavedChanges
    >
      {(control, _formApi, navigateWithoutBlocker) => (
        <VStack spacing={4} data-testid={props['data-testid']}>
          <Show when={duplicateError}>
            <Alert
              variant="error"
              title={i18n.t('entity.warehouse.labels.articleCategoryAlreadyExist')}
              data-testid={suffixTestId('duplicityAlert', props)}
            />
          </Show>

          <FormField
            isDisabled
            control={control}
            type="switch"
            name="isDefault"
            label={i18n.t('general.labels.default')}
            data-testid={suffixTestId('inputs.isDefault', props)}
          />

          <FormField
            isDisabled
            control={control}
            type="switch"
            name="active"
            label={i18n.t('general.labels.active')}
            data-testid={suffixTestId('inputs.isActive', props)}
          />

          <Separator />

          <Grid columns={4}>
            <FormField
              isRequired
              control={control}
              type="text"
              name="name"
              label={i18n.t('entity.warehouse.labels.name')}
              data-testid={suffixTestId('inputs.name', props)}
            />
            <FormField
              control={control}
              type="text"
              name="code"
              label={i18n.t('entity.warehouse.labels.code')}
              data-testid={suffixTestId('inputs.code', props)}
            />
          </Grid>

          <Space fillAvailable />

          <SettingsFooter
            actions={[
              {
                type: 'button',
                variant: 'secondary',
                title: isNil(props.articleCategory)
                  ? i18n.t('general.actions.discard')
                  : i18n.t('general.actions.discardChanges'),
                onClick: () => navigateWithoutBlocker(settingsRoutes.warehousesArticleCategory),
                'data-testid': suffixTestId('actions.discard', props),
              },
              {
                type: 'form-button',
                control,
                buttonType: 'submit',
                variant: 'primary',
                title: isNil(props.articleCategory)
                  ? i18n.t('general.actions.save')
                  : i18n.t('general.actions.saveChanges'),
                'data-testid': suffixTestId('actions.submit', props),
              },
            ]}
          />
        </VStack>
      )}
    </Form>
  );
}
